import { StrictMode, Suspense, useEffect, type ReactNode } from "react";
import { BrowserRouter, MemoryRouter, Route, Routes, useLocation } from "react-router-dom";
import { LocaleProvider, refreshDocumentLocale } from "./i18n/LocaleProvider";
import type { Locale } from "./i18n/locale-context";
import { LandingAuthProvider } from "./contexts/LandingAuth";
import { lazyRetry, PageFallback } from "./lib/lazyRetry";

// Eager: the public landing is prerendered to static HTML for /, /uz and /ru
// and hydrated in place (see main.tsx), so it must be in the entry chunk.
// Everything else (sign-in, the portal, Firebase) is one lazy chunk the landing
// never loads.
import { Landing } from "./pages/Landing";

const Portal = lazyRetry(() => import("./Portal"));

/** Keeps the tab title in step with the route: "OctoMail" inside /app, the
 *  localized marketing title elsewhere (landing pins still win). */
function RouteDocumentSync() {
  const { pathname } = useLocation();
  useEffect(() => {
    refreshDocumentLocale();
  }, [pathname]);
  return null;
}

/** One landing URL, pinned to its own language. */
function LandingRoute({ locale }: { locale: Locale }) {
  return (
    <LocaleProvider fixed={locale}>
      <LandingAuthProvider>
        <Landing />
      </LandingAuthProvider>
    </LocaleProvider>
  );
}

/**
 * The whole app. The build-time prerender (src/entry-prerender.tsx) renders
 * this exact tree with `ssrPath`, and the browser hydrates it with a
 * BrowserRouter: only the router differs, so the markup (and every useId) of
 * the landing matches and React keeps the prerendered DOM.
 */
export default function App({ ssrPath }: { ssrPath?: string }) {
  const routes: ReactNode = (
    <>
      <RouteDocumentSync />
      <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* Public. Each landing URL is pinned to its own language. */}
          <Route path="/" element={<LandingRoute locale="en" />} />
          <Route path="/uz" element={<LandingRoute locale="uz" />} />
          <Route path="/ru" element={<LandingRoute locale="ru" />} />
          {/* /login, /app/* and the catch-all redirect home. */}
          <Route path="*" element={<Portal />} />
        </Routes>
      </Suspense>
    </>
  );
  return (
    <StrictMode>
      <LocaleProvider>
        {ssrPath === undefined ? (
          <BrowserRouter>{routes}</BrowserRouter>
        ) : (
          <MemoryRouter initialEntries={[ssrPath]}>{routes}</MemoryRouter>
        )}
      </LocaleProvider>
    </StrictMode>
  );
}
